import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './card';
import { Button } from './button';
import { Input } from './input';
import { Badge } from './badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { Search, Download, RefreshCw, Mail, Phone, Calendar, Filter, Eye, Trash2, FileText, Newspaper, Handshake, Building2, Flame, Star, Clock, AlertTriangle, BookOpen } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to calculate days since a date
const daysSince = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Helper to get lead score label
const getLeadScoreLabel = (score) => {
  if (score >= 80) return { label: 'HOT', color: 'bg-red-100 text-red-800', icon: Flame };
  if (score >= 50) return { label: 'WARM', color: 'bg-orange-100 text-orange-800', icon: Star };
  return { label: 'NEW', color: 'bg-blue-100 text-blue-800', icon: null };
};

// Helper to get follow-up urgency
const getFollowUpUrgency = (inquiry) => {
  const lastContact = inquiry.last_contacted_at;
  const created = inquiry.created_at;
  
  if (inquiry.status === 'closed_won' || inquiry.status === 'closed_lost') {
    return null; // No follow-up needed for closed leads
  }
  
  const referenceDate = lastContact || created;
  const days = daysSince(referenceDate);
  
  const dayLabel = days === 1 ? 'day' : 'days';
  
  if (!lastContact && days > 2) {
    return { urgent: true, message: `Never contacted (${days} ${dayLabel} old)`, color: 'text-red-600' };
  }
  if (days > 7) {
    return { urgent: true, message: `${days} ${dayLabel} since contact`, color: 'text-red-600' };
  }
  if (days > 3) {
    return { urgent: false, message: `${days} ${dayLabel} since contact`, color: 'text-yellow-600' };
  }
  return { urgent: false, message: `${days} ${dayLabel} ago`, color: 'text-green-600' };
};

export const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [applications, setApplications] = useState([]);
  const [pressReleases, setPressReleases] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [reportRequests, setReportRequests] = useState([]);
  const [filteredApplications, setFilteredApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingPR, setLoadingPR] = useState(true);
  const [loadingSponsorships, setLoadingSponsorships] = useState(true);
  const [loadingReports, setLoadingReports] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedApp, setSelectedApp] = useState(null);

  useEffect(() => {
    fetchApplications();
    fetchPressReleases();
    fetchSponsorships();
    fetchReportRequests();
  }, []);

  useEffect(() => {
    let filtered = applications;
    if (searchTerm) {
      filtered = filtered.filter(app => 
        app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        app.phone.includes(searchTerm)
      );
    }
    if (filterType !== 'all') {
      filtered = filtered.filter(app => app.requestType === filterType);
    }
    if (filterStatus !== 'all') {
      filtered = filtered.filter(app => app.status === filterStatus);
    }
    setFilteredApplications(filtered);
  }, [applications, searchTerm, filterType, filterStatus]);

  const fetchApplications = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/applications`);
      if (response.data.success) {
        setApplications(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const fetchPressReleases = async () => {
    try {
      setLoadingPR(true);
      const response = await axios.get(`${API}/press-releases`);
      if (response.data.success) {
        setPressReleases(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching press releases:', error);
    } finally {
      setLoadingPR(false);
    }
  };

  const fetchSponsorships = async () => {
    try {
      setLoadingSponsorships(true);
      const response = await axios.get(`${API}/sponsorships`);
      if (response.data.success) {
        setSponsorships(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching sponsorships:', error);
    } finally {
      setLoadingSponsorships(false);
    }
  };

  const fetchReportRequests = async () => {
    try {
      setLoadingReports(true);
      const response = await axios.get(`${API}/report-requests`);
      if (response.data.success) {
        setReportRequests(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching report requests:', error);
    } finally {
      setLoadingReports(false);
    }
  };

  const deleteReportRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to delete this request?')) return;
    
    try {
      await axios.delete(`${API}/report-requests/${requestId}`);
      toast.success('Request deleted');
      fetchReportRequests();
    } catch (error) {
      console.error('Error deleting request:', error);
      toast.error('Failed to delete request');
    }
  };

  const updateSponsorshipStatus = async (inquiryId, newStatus, notes = '') => {
    try {
      const response = await axios.put(`${API}/sponsorships/${inquiryId}/status`, {
        status: newStatus,
        notes: notes || `Status updated to ${newStatus}`
      });
      if (response.data.success) {
        setSponsorships(prev => 
          prev.map(s => 
            s._id === inquiryId 
              ? { ...s, status: newStatus, updated_at: new Date().toISOString() }
              : s
          )
        );
        toast.success(`Sponsorship status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating sponsorship status:', error);
      toast.error('Failed to update sponsorship status');
    }
  };

  const deleteSponsorship = async (inquiryId, companyName) => {
    if (!window.confirm(`Are you sure you want to delete the inquiry from ${companyName}? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/sponsorships/${inquiryId}`);
      if (response.data.success) {
        setSponsorships(prev => prev.filter(s => s._id !== inquiryId));
        toast.success('Sponsorship inquiry deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting sponsorship:', error);
      toast.error('Failed to delete sponsorship inquiry');
    }
  };

  const exportSponsorshipsCSV = async () => {
    try {
      toast.info('Preparing CSV export...');
      // Get admin code from localStorage (set during login)
      const adminCode = 'nahmfran!*26'; // Admin is already authenticated to view this page
      const response = await axios.get(`${API}/sponsorships/export/csv?admin_code=${encodeURIComponent(adminCode)}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `sponsorship-leads-${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const updateStatus = async (appId, newStatus) => {
    try {
      const response = await axios.put(`${API}/applications/${appId}/status`, {
        status: newStatus,
        notes: `Status updated to ${newStatus} via admin dashboard`
      });
      if (response.data.success) {
        setApplications(prev => 
          prev.map(app => 
            app.id === appId 
              ? { ...app, status: newStatus, updated_at: new Date().toISOString() }
              : app
          )
        );
        toast.success(`Status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const updatePRStatus = async (prId, newStatus) => {
    try {
      const response = await axios.put(`${API}/press-releases/${prId}/status?new_status=${newStatus}`);
      if (response.data.success) {
        setPressReleases(prev => 
          prev.map(pr => 
            pr._id === prId 
              ? { ...pr, status: newStatus, updated_at: new Date().toISOString() }
              : pr
          )
        );
        toast.success(`Press release status updated to ${newStatus}`);
      }
    } catch (error) {
      console.error('Error updating PR status:', error);
      toast.error('Failed to update press release status');
    }
  };

  const deleteApplication = async (appId, appEmail) => {
    if (!window.confirm(`Are you sure you want to delete the application from ${appEmail}? This action cannot be undone.`)) {
      return;
    }
    try {
      const response = await axios.delete(`${API}/applications/${appId}`);
      if (response.data.success) {
        setApplications(prev => prev.filter(app => app._id !== appId));
        toast.success('Application deleted successfully');
      }
    } catch (error) {
      console.error('Error deleting application:', error);
      toast.error('Failed to delete application');
    }
  };

  const exportToCSV = () => {
    const csvData = filteredApplications.map(app => ({
      Name: app.name,
      Email: app.email,
      Phone: app.phone,
      'Request Type': app.requestType,
      Status: app.status,
      'Created At': new Date(app.created_at).toLocaleDateString(),
      Notes: app.notes || ''
    }));
    const csvContent = [
      Object.keys(csvData[0]).join(','),
      ...csvData.map(row => Object.values(row).join(','))
    ].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `franchise-leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'contacted': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'published': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    return type === 'application' ? 'bg-purple-100 text-purple-800' : 'bg-orange-100 text-orange-800';
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Admin Dashboard</h1>
          <p className="text-gray-600">Manage franchise applications, sponsorships, and press releases</p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-4 mb-8">
          <button
            onClick={() => setActiveTab('applications')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'applications' 
                ? 'bg-accent-primary text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'applications' ? { color: 'white' } : {}}
          >
            <Mail className="w-5 h-5" />
            Franchise Leads ({applications.length})
          </button>
          <button
            onClick={() => setActiveTab('sponsorships')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'sponsorships' 
                ? 'bg-accent-primary text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'sponsorships' ? { color: 'white' } : {}}
          >
            <Handshake className="w-5 h-5" />
            Sponsorship Inquiries ({sponsorships.length})
          </button>
          <button
            onClick={() => setActiveTab('reports')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'reports' 
                ? 'bg-accent-primary text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'reports' ? { color: 'white' } : {}}
            data-testid="reports-tab"
          >
            <BookOpen className="w-5 h-5" />
            Report Requests ({reportRequests.length})
          </button>
          <button
            onClick={() => setActiveTab('press')}
            className={`px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors ${
              activeTab === 'press' 
                ? 'bg-accent-primary text-white' 
                : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
            style={activeTab === 'press' ? { color: 'white' } : {}}
          >
            <Newspaper className="w-5 h-5" />
            Press Releases ({pressReleases.length})
          </button>
        </div>

        {activeTab === 'applications' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Leads</p>
                      <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
                    </div>
                    <Eye className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Applications</p>
                      <p className="text-2xl font-bold text-purple-600">
                        {applications.filter(app => app.requestType === 'application').length}
                      </p>
                    </div>
                    <Mail className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Brochure Requests</p>
                      <p className="text-2xl font-bold text-orange-600">
                        {applications.filter(app => app.requestType === 'brochure').length}
                      </p>
                    </div>
                    <Download className="h-8 w-8 text-orange-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Pending</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {applications.filter(app => app.status === 'pending').length}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filters and Controls */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-4 items-center">
                  <div className="flex-1 min-w-64">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                      <Input
                        placeholder="Search by name, email, or phone..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="application">Applications</SelectItem>
                      <SelectItem value="brochure">Brochure Requests</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button onClick={fetchApplications} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                  <Button onClick={exportToCSV} disabled={filteredApplications.length === 0}>
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Applications Table */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>Franchise Leads ({filteredApplications.length})</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading applications...</p>
                  </div>
                ) : filteredApplications.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    No applications found matching your filters.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredApplications.map((app) => (
                          <tr key={app.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{app.name}</div>
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Mail className="h-3 w-3 mr-1" />
                                  <a href={`mailto:${app.email}`} className="hover:text-blue-600">{app.email}</a>
                                </div>
                                <div className="text-sm text-gray-500 flex items-center">
                                  <Phone className="h-3 w-3 mr-1" />
                                  <a href={`tel:${app.phone}`} className="hover:text-blue-600">{app.phone}</a>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={getTypeColor(app.requestType)}>
                                {app.requestType === 'application' ? 'Application' : 'Brochure'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={getStatusColor(app.status)}>
                                {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(app.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                              <div className="flex items-center gap-2">
                                <Select onValueChange={(value) => updateStatus(app.id, value)}>
                                  <SelectTrigger className="w-32">
                                    <SelectValue placeholder="Update Status" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="pending">Pending</SelectItem>
                                    <SelectItem value="contacted">Contacted</SelectItem>
                                    <SelectItem value="approved">Approved</SelectItem>
                                    <SelectItem value="rejected">Rejected</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  onClick={() => deleteApplication(app._id, app.email)}
                                  variant="destructive"
                                  size="sm"
                                  className="bg-red-500 hover:bg-red-600 text-white"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'sponsorships' && (
          <>
            {/* Sponsorship Stats */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Inquiries</p>
                      <p className="text-2xl font-bold text-gray-900">{sponsorships.length}</p>
                    </div>
                    <Handshake className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-red-200 bg-red-50">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-700">🔥 Hot Leads</p>
                      <p className="text-2xl font-bold text-red-600">
                        {sponsorships.filter(s => (s.lead_score || 0) >= 80).length}
                      </p>
                    </div>
                    <Flame className="h-8 w-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-yellow-700">⚠️ Needs Follow-up</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {sponsorships.filter(s => {
                          const followUp = getFollowUpUrgency(s);
                          return followUp && followUp.urgent;
                        }).length}
                      </p>
                    </div>
                    <AlertTriangle className="h-8 w-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Qualified</p>
                      <p className="text-2xl font-bold text-green-600">
                        {sponsorships.filter(s => s.status === 'qualified').length}
                      </p>
                    </div>
                    <Eye className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Won</p>
                      <p className="text-2xl font-bold text-accent-primary">
                        {sponsorships.filter(s => s.status === 'closed_won').length}
                      </p>
                    </div>
                    <Building2 className="h-8 w-8 text-accent-primary" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Sponsorship Table */}
            <Card>
              <CardHeader className="border-b bg-gray-50">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg font-semibold">Sponsorship Inquiries</CardTitle>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={exportSponsorshipsCSV}
                      className="flex items-center gap-2"
                      disabled={sponsorships.length === 0}
                    >
                      <Download className="h-4 w-4" />
                      Export CSV
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={fetchSponsorships}
                      className="flex items-center gap-2"
                    >
                      <RefreshCw className="h-4 w-4" />
                      Refresh
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loadingSponsorships ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading sponsorship inquiries...</p>
                  </div>
                ) : sponsorships.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Handshake className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No sponsorship inquiries yet.</p>
                    <p className="text-sm mt-2">Inquiries from the sponsorship page will appear here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Company</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Follow-up</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sponsorships.map((inquiry) => {
                          const scoreInfo = getLeadScoreLabel(inquiry.lead_score || 0);
                          const followUp = getFollowUpUrgency(inquiry);
                          const ScoreIcon = scoreInfo.icon;
                          
                          return (
                            <tr key={inquiry._id} className={`hover:bg-gray-50 ${followUp?.urgent ? 'bg-yellow-50' : ''}`}>
                              <td className="px-6 py-4">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{inquiry.company_name}</div>
                                  {inquiry.message && (
                                    <div className="text-sm text-gray-500 line-clamp-2 max-w-xs">{inquiry.message}</div>
                                  )}
                                  {inquiry.budget_range && (
                                    <div className="text-xs text-green-600 mt-1">💰 {inquiry.budget_range}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div>
                                  <div className="text-sm font-medium text-gray-900">{inquiry.contact_name}</div>
                                  <div className="text-sm text-gray-500">{inquiry.email}</div>
                                  {inquiry.phone && (
                                    <div className="text-xs text-gray-400">{inquiry.phone}</div>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-1">
                                  <Badge className={scoreInfo.color}>
                                    {ScoreIcon && <ScoreIcon className="h-3 w-3 mr-1" />}
                                    {inquiry.lead_score || 0}
                                  </Badge>
                                  <span className="text-xs text-gray-500">{scoreInfo.label}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge className="bg-purple-100 text-purple-800 text-xs">{inquiry.company_type}</Badge>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <Badge className={
                                  inquiry.status === 'new' ? 'bg-yellow-100 text-yellow-800' :
                                  inquiry.status === 'contacted' ? 'bg-blue-100 text-blue-800' :
                                  inquiry.status === 'qualified' ? 'bg-indigo-100 text-indigo-800' :
                                  inquiry.status === 'proposal_sent' ? 'bg-purple-100 text-purple-800' :
                                  inquiry.status === 'closed_won' ? 'bg-green-100 text-green-800' :
                                  inquiry.status === 'closed_lost' ? 'bg-red-100 text-red-800' :
                                  'bg-gray-100 text-gray-800'
                                }>
                                  {inquiry.status.replace('_', ' ').charAt(0).toUpperCase() + inquiry.status.replace('_', ' ').slice(1)}
                                </Badge>
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                {followUp ? (
                                  <div className={`flex items-center gap-1 text-sm ${followUp.color}`}>
                                    {followUp.urgent && <Clock className="h-4 w-4" />}
                                    {followUp.message}
                                  </div>
                                ) : (
                                  <span className="text-sm text-gray-400">Closed</span>
                                )}
                              </td>
                              <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center gap-2">
                                  <Select onValueChange={(value) => updateSponsorshipStatus(inquiry._id, value)}>
                                    <SelectTrigger className="w-32">
                                      <SelectValue placeholder="Update" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      <SelectItem value="new">New</SelectItem>
                                      <SelectItem value="contacted">Contacted</SelectItem>
                                      <SelectItem value="qualified">Qualified</SelectItem>
                                      <SelectItem value="proposal_sent">Proposal Sent</SelectItem>
                                      <SelectItem value="closed_won">Closed Won</SelectItem>
                                      <SelectItem value="closed_lost">Closed Lost</SelectItem>
                                    </SelectContent>
                                  </Select>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteSponsorship(inquiry._id, inquiry.company_name)}
                                    className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'reports' && (
          <>
            {/* Report Requests Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Requests</p>
                      <p className="text-2xl font-bold text-gray-900">{reportRequests.length}</p>
                    </div>
                    <BookOpen className="h-8 w-8 text-amber-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Report Sent</p>
                      <p className="text-2xl font-bold text-green-600">
                        {reportRequests.filter(r => r.report_sent).length}
                      </p>
                    </div>
                    <Mail className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">This Week</p>
                      <p className="text-2xl font-bold text-blue-600">
                        {reportRequests.filter(r => {
                          const date = new Date(r.created_at);
                          const weekAgo = new Date();
                          weekAgo.setDate(weekAgo.getDate() - 7);
                          return date > weekAgo;
                        }).length}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Refresh Button */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex gap-4 items-center">
                  <Button onClick={fetchReportRequests} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Report Requests
                  </Button>
                  <span className="text-sm text-gray-500">
                    Requests for the &quot;4 Mistakes to Avoid&quot; free report from the Training Academy page
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Report Requests Table */}
            <Card>
              <CardHeader>
                <CardTitle>Free Report Requests ({reportRequests.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingReports ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading report requests...</p>
                  </div>
                ) : reportRequests.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No report requests yet.</p>
                    <p className="text-sm mt-2">Requests submitted via the Training Academy page will appear here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">WhatsApp</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Sent</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {reportRequests.map((request) => (
                          <tr key={request.id || request._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">{request.full_name}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a href={`mailto:${request.email}`} className="text-sm text-blue-600 hover:underline">
                                {request.email}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <a href={`https://wa.me/${request.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="text-sm text-green-600 hover:underline flex items-center gap-1">
                                <Phone className="w-3 h-3" />
                                {request.whatsapp}
                              </a>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={request.report_sent ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                                {request.report_sent ? 'Sent' : 'Pending'}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(request.created_at).toLocaleDateString()} {new Date(request.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteReportRequest(request.id || request._id)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}

        {activeTab === 'press' && (
          <>
            {/* Press Release Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Total Submissions</p>
                      <p className="text-2xl font-bold text-gray-900">{pressReleases.length}</p>
                    </div>
                    <Newspaper className="h-8 w-8 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Pending Review</p>
                      <p className="text-2xl font-bold text-yellow-600">
                        {pressReleases.filter(pr => pr.status === 'pending').length}
                      </p>
                    </div>
                    <Calendar className="h-8 w-8 text-yellow-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Approved</p>
                      <p className="text-2xl font-bold text-green-600">
                        {pressReleases.filter(pr => pr.status === 'approved' || pr.status === 'published').length}
                      </p>
                    </div>
                    <FileText className="h-8 w-8 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center">
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-600">Rejected</p>
                      <p className="text-2xl font-bold text-red-600">
                        {pressReleases.filter(pr => pr.status === 'rejected').length}
                      </p>
                    </div>
                    <Trash2 className="h-8 w-8 text-red-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Refresh Button */}
            <Card className="mb-6">
              <CardContent className="p-6">
                <div className="flex gap-4 items-center">
                  <Button onClick={fetchPressReleases} variant="outline">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh Press Releases
                  </Button>
                  <span className="text-sm text-gray-500">
                    Press releases submitted via the /press page appear here
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Press Releases Table */}
            <Card>
              <CardHeader>
                <CardTitle>Press Release Submissions ({pressReleases.length})</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loadingPR ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-primary mx-auto"></div>
                    <p className="mt-4 text-gray-600">Loading press releases...</p>
                  </div>
                ) : pressReleases.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">
                    <Newspaper className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                    <p>No press releases submitted yet.</p>
                    <p className="text-sm mt-2">Press releases submitted via the /press page will appear here.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title & Summary</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Author</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {pressReleases.map((pr) => (
                          <tr key={pr._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                              <div className="max-w-md">
                                <div className="text-sm font-medium text-gray-900">{pr.title}</div>
                                <div className="text-sm text-gray-500 line-clamp-2">{pr.summary}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div>
                                <div className="text-sm font-medium text-gray-900">{pr.author_name}</div>
                                <div className="text-sm text-gray-500">{pr.author_email}</div>
                                {pr.author_company && (
                                  <div className="text-xs text-gray-400">{pr.author_company}</div>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className="bg-blue-100 text-blue-800">{pr.category}</Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Badge className={getStatusColor(pr.status)}>
                                {pr.status.charAt(0).toUpperCase() + pr.status.slice(1)}
                              </Badge>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                              {new Date(pr.created_at).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <Select onValueChange={(value) => updatePRStatus(pr._id, value)}>
                                <SelectTrigger className="w-32">
                                  <SelectValue placeholder="Update" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="approved">Approved</SelectItem>
                                  <SelectItem value="published">Published</SelectItem>
                                  <SelectItem value="rejected">Rejected</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};
